/** Stock & Inventory — Phase 3 tabs (aging, reorder, daily reports) +
 *  Morning Stock Brief (shown on first login each day). */
import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Sunrise, FileDown, X } from "lucide-react";
import { BrandBar, CategoryBar, FilterChips } from "./StockBrandBar";

export function AgingTab() {
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/inventory/aging").then(({ data }) => setD(data)); }, []);
  return (
    <div className="border border-slate-200 rounded-lg bg-white overflow-x-auto" data-testid="inv-aging">
      <div className="px-3 py-2 border-b border-slate-100 text-[11px] font-semibold text-slate-700">
        Stock aging — buckets {d ? d.buckets.join(" / ") : "…"} days · slow-moving ≥ {d?.slow_moving_days}d without movement
      </div>
      <table className="w-full text-[11px]">
        <thead className="bg-slate-50 text-slate-500"><tr>
          <th className="text-left px-3 py-2">Product / Warehouse</th>
          <th className="text-right px-3 py-2">Qty</th>
          <th className="text-right px-3 py-2">Oldest (days)</th>
          <th className="text-left px-3 py-2">Buckets</th>
          <th className="text-left px-3 py-2">Flags</th></tr></thead>
        <tbody>
          {(!d || !d.items.length) && <tr><td colSpan={5} className="text-center text-slate-400 px-3 py-6">No stock to age.</td></tr>}
          {(d?.items || []).map((r, i) => (
            <tr key={i} className="border-t border-slate-100" data-testid={`aging-row-${i}`}>
              <td className="px-3 py-2">{r.product_name}<div className="text-[9px] text-slate-400">{r.brand}</div></td>
              <td className="px-3 py-2 text-right font-mono">{r.quantity}</td>
              <td className="px-3 py-2 text-right font-mono">{r.oldest_days}</td>
              <td className="px-3 py-2 text-[10px] font-mono">
                {Object.entries(r.buckets || {}).map(([b, q]) => `${b}d:${q}`).join("  ")}</td>
              <td className="px-3 py-2">{(r.flags || []).map((f) =>
                <span key={f} className="text-[8.5px] px-1 py-0.5 mr-1 rounded bg-amber-100 text-amber-800">{f.replace(/_/g, " ")}</span>)}</td>
            </tr>))}
        </tbody>
      </table>
    </div>
  );
}

export function ReorderTab() {
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/inventory/reorder").then(({ data }) => setD(data)); }, []);
  return (
    <div className="border border-slate-200 rounded-lg bg-white" data-testid="inv-reorder">
      <div className="px-3 py-2 border-b border-slate-100 text-[11px] font-semibold text-slate-700">
        Reorder status — set thresholds on products via Product Master inventory settings
        {d?.reorder_required_count > 0 && (
          <span className="ml-2 text-[9px] px-1.5 py-0.5 rounded bg-rose-100 text-rose-800"
              data-testid="reorder-count">{d.reorder_required_count} below reorder level</span>)}
      </div>
      <table className="w-full text-[11px]">
        <thead className="bg-slate-50 text-slate-500"><tr>
          <th className="text-left px-3 py-2">Product</th>
          <th className="text-right px-3 py-2">Available</th>
          <th className="text-right px-3 py-2">Reorder level</th>
          <th className="text-right px-3 py-2">Target</th>
          <th className="text-left px-3 py-2">Preferred supplier</th>
          <th className="text-left px-3 py-2"></th></tr></thead>
        <tbody>
          {(!d || !d.items.length) && <tr><td colSpan={6} className="text-center text-slate-400 px-3 py-6">
            No products have reorder levels configured yet.</td></tr>}
          {(d?.items || []).map((r) => (
            <tr key={r.product_id} className={"border-t border-slate-100 " + (r.reorder_required ? "bg-rose-50/50" : "")}
                data-testid={`reorder-row-${r.product_id}`}>
              <td className="px-3 py-2">{r.product_name}</td>
              <td className="px-3 py-2 text-right font-mono">{r.available}</td>
              <td className="px-3 py-2 text-right font-mono">{r.reorder_level}</td>
              <td className="px-3 py-2 text-right font-mono">{r.target_stock ?? "—"}</td>
              <td className="px-3 py-2 text-[10px]">{r.preferred_supplier || "—"}</td>
              <td className="px-3 py-2">{r.reorder_required &&
                <span className="text-[9px] font-semibold px-1.5 py-0.5 rounded bg-rose-100 text-rose-800">REORDER REQUIRED</span>}</td>
            </tr>))}
        </tbody>
      </table>
    </div>
  );
}

export function DailyReportsTab({ canWrite }) {
  const [snaps, setSnaps] = useState([]);
  const [report, setReport] = useState(null);
  const load = () => api.get("/inventory/snapshots").then(({ data }) => setSnaps(data.items || []));
  useEffect(() => {
    load();
    api.get("/inventory/daily-report").then(({ data }) => setReport(data));
  }, []);
  const run = async () => {
    try {
      const { data } = await api.post("/inventory/snapshots/run");
      toast.success(`Snapshot ${data.snapshot.date} — ${data.snapshot.reconciled ? "reconciled ✓" : "VARIANCE " + data.snapshot.variance}`);
      load();
      api.get("/inventory/daily-report").then(({ data }) => setReport(data));
    } catch (e) { toast.error(e.response?.data?.detail || "Snapshot failed"); }
  };
  const dl = async (fmt) => {
    try {
      const res = await api.get(`/inventory/daily-report/${fmt}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = `stock-report.${fmt}`; a.click(); URL.revokeObjectURL(url);
    } catch { toast.error("Download failed"); }
  };
  return (
    <div className="space-y-3" data-testid="inv-daily-reports">
      <div className="flex items-center gap-2">
        {canWrite && <button onClick={run} data-testid="snapshot-run"
            className="text-[11px] px-2.5 py-1.5 rounded-md bg-slate-900 text-white">Run today's snapshot</button>}
        <button onClick={() => dl("pdf")} data-testid="report-pdf"
            className="text-[11px] px-2.5 py-1.5 rounded-md border border-slate-300 inline-flex items-center gap-1">
          <FileDown className="h-3 w-3" /> PDF</button>
        <button onClick={() => dl("xlsx")} data-testid="report-xlsx"
            className="text-[11px] px-2.5 py-1.5 rounded-md border border-slate-300 inline-flex items-center gap-1">
          <FileDown className="h-3 w-3" /> XLSX</button>
      </div>
      {report && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2" data-testid="daily-report-cards">
          {["opening", "stock_in", "stock_out", "closing"].map((k) => (
            <div key={k} className="border border-slate-200 rounded-lg bg-white p-3">
              <div className="text-[10px] uppercase text-slate-500">{k.replace("_", " ")}</div>
              <div className="text-lg font-heading font-semibold">{report.snapshot?.[k] ?? "—"}</div>
            </div>))}
        </div>)}
      <div className="border border-slate-200 rounded-lg bg-white">
        <div className="px-3 py-2 border-b border-slate-100 text-[11px] font-semibold text-slate-700">
          Daily snapshots — reproducible from the movement ledger
        </div>
        <table className="w-full text-[11px]">
          <thead className="bg-slate-50 text-slate-500"><tr>
            <th className="text-left px-3 py-2">Date</th><th className="text-right px-3 py-2">Opening</th>
            <th className="text-right px-3 py-2">In</th><th className="text-right px-3 py-2">Out</th>
            <th className="text-right px-3 py-2">Closing</th><th className="text-right px-3 py-2">Available</th>
            <th className="text-left px-3 py-2">Reconciliation</th></tr></thead>
          <tbody>
            {snaps.length === 0 && <tr><td colSpan={7} className="text-center text-slate-400 px-3 py-6">No snapshots yet — run today's snapshot.</td></tr>}
            {snaps.map((s) => (
              <tr key={s.id} className="border-t border-slate-100" data-testid={`snap-row-${s.date}`}>
                <td className="px-3 py-2 font-mono">{s.date}</td>
                <td className="px-3 py-2 text-right font-mono">{s.opening}</td>
                <td className="px-3 py-2 text-right font-mono text-emerald-800">+{s.stock_in}</td>
                <td className="px-3 py-2 text-right font-mono text-rose-700">−{s.stock_out}</td>
                <td className="px-3 py-2 text-right font-mono font-semibold">{s.closing}</td>
                <td className="px-3 py-2 text-right font-mono">{s.available}</td>
                <td className="px-3 py-2">{s.reconciled
                  ? <span className="text-[9px] px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-800">RECONCILED</span>
                  : <span className="text-[9px] px-1.5 py-0.5 rounded bg-rose-100 text-rose-800">VARIANCE {s.variance}</span>}</td>
              </tr>))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export function StockReportTab({ canWrite }) {
  const [d, setD] = useState(null);
  const [dep, setDep] = useState("");
  const [kind, setKind] = useState("DEVICE");
  const [brand, setBrand] = useState("");
  const [category, setCategory] = useState("");
  const [refresh, setRefresh] = useState(0);
  const reload = () => setRefresh((x) => x + 1);
  useEffect(() => {
    api.get("/inventory/stock-report", { params: { dep: dep || undefined, kind, brand: brand || undefined, category: category || undefined } })
      .then(({ data }) => setD(data));
  }, [dep, kind, brand, category, refresh]);
  const dl = async (fmt) => {
    try {
      const res = await api.get(`/inventory/stock-report/${fmt}`,
        { params: { dep: dep || undefined, kind, brand: brand || undefined, category: category || undefined }, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      const base = kind === "ACCESSORY" ? "accessories" : "stock";
      a.href = url; a.download = `suntronix-${base}-report.${fmt}`; a.click(); URL.revokeObjectURL(url);
    } catch { toast.error("Download failed"); }
  };
  const catLabel = (d?.category_summary || []).find((c) => c.key === category)?.label || category;
  const brandLabel = (d?.brand_summary || []).find((b) => b.key === brand)?.label || brand;
  const depLabel = dep === "YES" ? "DEP" : dep === "NO" ? "Non-DEP" : "";
  const bands = ["bg-orange-100", "bg-green-100", "bg-sky-100", "bg-amber-100",
    "bg-pink-100", "bg-indigo-100"];
  const fmtM = (v) => v ? Number(v).toLocaleString("en-IN", { minimumFractionDigits: 2 }) : "";
  const isAcc = kind === "ACCESSORY";
  const title = isAcc ? "SUNTRONIX ACCESSORIES STOCK" : "SUNTRONIX STOCK REPORT";
  return (
    <div className="space-y-3" data-testid="inv-stock-report">
      <div className="inline-flex rounded-lg border border-slate-300 overflow-hidden" data-testid="sr-kind-switch">
        {[["DEVICE", "Devices"], ["ACCESSORY", "Accessories"]].map(([k, lbl]) => (
          <button key={k} onClick={() => { setKind(k); setBrand(""); setCategory(""); }} data-testid={`sr-kind-${k}`}
              className={"text-[11px] px-4 py-1.5 font-medium transition-colors " +
                (kind === k ? "bg-slate-900 text-white" : "bg-white text-slate-600 hover:bg-slate-50")}>
            {lbl}</button>))}
      </div>
      <CategoryBar categorySummary={d?.category_summary || []}
          value={category} onChange={(v) => { setCategory(v); setBrand(""); }} />
      <BrandBar brandSummary={d?.brand_summary || []} allBrands={d?.all_brands || []}
          value={brand} onChange={setBrand} />
      <FilterChips
          chips={[
            { key: "category", label: "Category", value: category, display: catLabel },
            { key: "brand", label: "Brand", value: brand, display: brandLabel },
            { key: "dep", label: "DEP", value: dep, display: depLabel },
          ]}
          onClear={(k) => { if (k === "category") { setCategory(""); setBrand(""); } else if (k === "brand") setBrand(""); else setDep(""); }}
          onClearAll={() => { setCategory(""); setBrand(""); setDep(""); }} />
      <div className="flex items-center gap-2 flex-wrap">
        <button onClick={() => dl("pdf")} data-testid="sr-pdf"
            className="text-[11px] px-2.5 py-1.5 rounded-md border border-slate-300 inline-flex items-center gap-1">
          <FileDown className="h-3 w-3" /> PDF</button>
        <button onClick={() => dl("xlsx")} data-testid="sr-xlsx"
            className="text-[11px] px-2.5 py-1.5 rounded-md border border-slate-300 inline-flex items-center gap-1">
          <FileDown className="h-3 w-3" /> XLSX</button>
        <select value={dep} onChange={(e) => setDep(e.target.value)} data-testid="sr-dep-filter"
            className="text-[11px] h-8 border border-slate-300 rounded-md px-2 bg-white">
          <option value="">DEP Stock: All</option>
          <option value="YES">DEP Stock: Yes</option>
          <option value="NO">DEP Stock: No</option>
        </select>
        {d && <span className="text-[11px] text-slate-500 ml-2">
          {d.total_skus} SKUs · {d.total_qty} units</span>}
        {canWrite && <span className="text-[10px] text-slate-400 ml-auto">Tip: click a DEP or Non-DEP number to reclassify</span>}
      </div>
      <div className="border-2 border-slate-800 rounded-sm bg-white overflow-x-auto">
        <table className="w-full text-[11.5px]">
          <thead>
            <tr className="border-b-2 border-slate-800">
              <th className="text-left px-3 py-2 w-36 border-r border-slate-400">Part No.</th>
              <th className="text-center px-3 py-2 border-r border-slate-400 font-heading text-[13px]">
                {title}</th>
              <th className="text-center px-3 py-2 w-20 border-r border-slate-400">DEP</th>
              <th className="text-center px-3 py-2 w-20 border-r border-slate-400">Non-DEP</th>
              <th className="text-center px-3 py-2 w-16 border-r border-slate-400">Total</th>
              <th className="text-right px-3 py-2 w-28">{d?.date ? d.date.split("-").reverse().join(".") : "MRP"}</th>
            </tr>
            <tr className="border-b border-slate-500 bg-slate-50 text-slate-600">
              <th className="text-left px-3 py-1 border-r border-slate-300">Part No.</th>
              <th className="text-left px-3 py-1 border-r border-slate-300">{isAcc ? "Description / Brand" : "Description"}</th>
              <th className="text-center px-3 py-1 border-r border-slate-300">DEP</th>
              <th className="text-center px-3 py-1 border-r border-slate-300">Non-DEP</th>
              <th className="text-center px-3 py-1 border-r border-slate-300">Total</th>
              <th className="text-right px-3 py-1">MRP</th>
            </tr>
          </thead>
          <tbody>
            {(!d || !d.groups.length) && (
              <tr><td colSpan={6} className="text-center text-slate-400 px-3 py-10" data-testid="sr-empty">
                {isAcc
                  ? "No accessories in stock yet — accessory products appear here as you record inward stock."
                  : "No stock yet — the report builds itself as you record inward / outward / reservations."}</td></tr>)}
            {(d?.groups || []).map((g, gi) => (
              <FragmentGroup key={g.group} g={g} band={bands[gi % bands.length]} fmtM={fmtM}
                  canWrite={canWrite} onChanged={reload} showBrand={isAcc} />))}
            {d?.groups?.length > 0 && (
              <tr className="border-t-2 border-slate-800 font-bold">
                <td className="px-3 py-1.5"></td>
                <td className="px-3 py-1.5 text-right">TOTAL</td>
                <td className="px-3 py-1.5 text-center font-mono text-emerald-700" data-testid="sr-total-dep">{d.total_dep}</td>
                <td className="px-3 py-1.5 text-center font-mono text-sky-700" data-testid="sr-total-nondep">{d.total_nondep}</td>
                <td className="px-3 py-1.5 text-center font-mono" data-testid="sr-total">{d.total_qty}</td>
                <td></td></tr>)}
          </tbody>
        </table>
      </div>
    </div>
  );
}

const DEP_BUCKETS = [["YES", "Yes (DEP)"], ["NO", "No (NON-DEP)"], ["REQUIRED", "Not classified"]];

function DepNumCell({ value, tone, onClick, disabled, testid }) {
  const cls = tone === "dep" ? "text-emerald-700" : tone === "non" ? "text-sky-700" : "text-slate-800";
  if (disabled || !onClick)
    return <span className={"font-mono " + cls} data-testid={testid}>{value}</span>;
  return (
    <button data-testid={testid} onClick={onClick}
        className={"font-mono px-2 py-0.5 rounded hover:bg-slate-100 hover:ring-1 hover:ring-slate-300 transition-colors " + cls}>
      {value}</button>);
}

function StockRow({ r, canWrite, onChanged, showBrand }) {
  const [edit, setEdit] = useState(false);
  const open = canWrite ? () => setEdit(true) : undefined;
  return (
    <>
      <tr className="border-b border-slate-200" data-testid={`sr-row-${r.part_no}`}>
        <td className="px-3 py-1 font-mono font-semibold border-r border-slate-200">{r.part_no}</td>
        <td className="px-3 py-1 border-r border-slate-200">
          {r.description}
          {showBrand && r.brand && <div className="text-[10px] text-slate-500">{r.brand}</div>}
        </td>
        <td className="px-3 py-1 text-center border-r border-slate-200">
          <DepNumCell value={r.dep_qty} tone="dep" onClick={open} disabled={!canWrite} testid={`sr-dep-${r.part_no}`} /></td>
        <td className="px-3 py-1 text-center border-r border-slate-200">
          <DepNumCell value={r.nondep_qty} tone="non" onClick={open} disabled={!canWrite} testid={`sr-nondep-${r.part_no}`} /></td>
        <td className="px-3 py-1 text-center border-r border-slate-200">
          <DepNumCell value={r.total} tone="total" onClick={open} disabled={!canWrite} testid={`sr-tot-${r.part_no}`} />
          {r.unclassified_qty > 0 && (
            <span onClick={open} data-testid={`sr-unc-${r.part_no}`}
                className={"ml-1 inline-block text-[8px] font-semibold px-1 py-0.5 rounded bg-amber-100 text-amber-800 " + (canWrite ? "cursor-pointer" : "")}>
              {r.unclassified_qty} to classify</span>)}
        </td>
        <td className="px-3 py-1 text-right font-mono">{r.mrp ? Number(r.mrp).toLocaleString("en-IN", { minimumFractionDigits: 2 }) : ""}</td>
      </tr>
      {edit && <DepReclassifyModal r={r} onClose={() => setEdit(false)} onChanged={onChanged} />}
    </>
  );
}

function DepReclassifyModal({ r, onClose, onChanged }) {
  const isSerial = r.tracking === "SERIAL";
  const [busy, setBusy] = useState(false);
  const serials = r.serials || { YES: [], NO: [], REQUIRED: [] };
  const [assign, setAssign] = useState(() => {
    const m = {};
    (serials.YES || []).forEach((s) => { m[s] = "YES"; });
    (serials.NO || []).forEach((s) => { m[s] = "NO"; });
    (serials.REQUIRED || []).forEach((s) => { m[s] = "REQUIRED"; });
    return m;
  });
  const allSerials = Object.keys(assign);
  const [counts, setCounts] = useState({ YES: r.dep_qty, NO: r.nondep_qty, REQUIRED: r.unclassified_qty });
  const lotSum = counts.YES + counts.NO + counts.REQUIRED;
  const setCount = (k, v) => {
    const n = Math.max(0, Math.min(r.total, parseInt(v || "0", 10) || 0));
    setCounts((c) => ({ ...c, [k]: n }));
  };
  const setAll = (b) => setAssign(Object.fromEntries(allSerials.map((s) => [s, b])));

  const save = async () => {
    setBusy(true);
    try {
      let payload;
      if (isSerial) {
        const groups = { YES: [], NO: [], REQUIRED: [] };
        Object.entries(assign).forEach(([s, t]) => groups[t].push(s));
        const moves = Object.entries(groups).filter(([, a]) => a.length)
          .map(([t, a]) => ({ to_dep: t, serials: a }));
        payload = { product_id: r.product_id, moves };
      } else {
        if (lotSum !== r.total) { toast.error(`Quantities must total ${r.total}`); setBusy(false); return; }
        payload = { product_id: r.product_id, set_distribution: counts };
      }
      await api.post("/inventory/classify-dep", payload);
      toast.success("DEP classification updated");
      onChanged(); onClose();
    } catch (e) { toast.error(e.response?.data?.detail || "Update failed"); }
    setBusy(false);
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" data-testid="dep-reclassify-modal">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-md max-h-[85vh] flex flex-col">
        <div className="flex items-center gap-2 px-4 py-3 border-b border-slate-100">
          <div className="text-[13px] font-heading font-semibold">Classify DEP stock</div>
          <div className="flex-1" />
          <button onClick={onClose} data-testid="dep-close" className="text-slate-400 hover:text-slate-700"><X className="h-4 w-4" /></button>
        </div>
        <div className="px-4 py-3 overflow-y-auto text-[12px]">
          <div className="text-slate-600 mb-3">
            <span className="font-mono font-semibold">{r.part_no}</span> — {r.description}
            <div className="text-[11px] text-slate-400 mt-0.5">
              {r.total} unit(s) · DEP {r.dep_qty} · Non-DEP {r.nondep_qty} · Not classified {r.unclassified_qty}</div>
          </div>
          {isSerial ? (
            <>
              <div className="flex items-center gap-1.5 mb-2 text-[10px]">
                <span className="text-slate-400">Set all:</span>
                {DEP_BUCKETS.map(([b, lbl]) => (
                  <button key={b} onClick={() => setAll(b)} data-testid={`dep-all-${b}`}
                      className="px-2 py-0.5 rounded border border-slate-200 hover:bg-slate-50">{lbl}</button>))}
              </div>
              <div className="space-y-1.5 max-h-[45vh] overflow-y-auto" data-testid="dep-serials">
                {allSerials.map((s) => (
                  <div key={s} className="flex items-center gap-2 border border-slate-100 rounded-md px-2 py-1.5">
                    <span className="font-mono text-[11px] flex-1 truncate">{s}</span>
                    <select value={assign[s]} onChange={(e) => setAssign((a) => ({ ...a, [s]: e.target.value }))}
                        data-testid={`dep-serial-${s}`}
                        className="text-[11px] h-7 border border-slate-300 rounded px-1.5 bg-white">
                      <option value="YES">Yes (DEP)</option>
                      <option value="NO">No (NON-DEP)</option>
                      <option value="REQUIRED">Not classified</option>
                    </select>
                  </div>))}
              </div>
            </>
          ) : (
            <div className="space-y-2" data-testid="dep-lot">
              {DEP_BUCKETS.map(([k, lbl]) => (
                <div key={k} className="flex items-center gap-2">
                  <label className="flex-1 text-slate-600">{lbl}</label>
                  <input type="number" min="0" max={r.total} value={counts[k]}
                      onChange={(e) => setCount(k, e.target.value)} data-testid={`dep-lot-qty-${k}`}
                      className="w-20 text-right text-[12px] h-8 border border-slate-300 rounded px-2 font-mono" />
                </div>))}
              <div className={"flex items-center justify-between text-[11px] pt-1 border-t border-slate-100 " + (lotSum === r.total ? "text-emerald-700" : "text-rose-600")}>
                <span>Total assigned</span>
                <span className="font-mono font-semibold" data-testid="dep-lot-sum">{lotSum} / {r.total}</span>
              </div>
            </div>
          )}
        </div>
        <div className="flex items-center justify-end gap-2 px-4 py-3 border-t border-slate-100">
          <button onClick={onClose} className="text-[11px] px-3 py-1.5 rounded-md border border-slate-300">Cancel</button>
          <button onClick={save} disabled={busy || (!isSerial && lotSum !== r.total)} data-testid="dep-save"
              className="text-[11px] px-3 py-1.5 rounded-md bg-slate-900 text-white disabled:opacity-40">
            {busy ? "Saving…" : "Save"}</button>
        </div>
      </div>
    </div>
  );
}

function FragmentGroup({ g, band, fmtM, canWrite, onChanged, showBrand }) {
  return (
    <>
      <tr className={band + " border-y border-slate-400"} data-testid={`sr-group-${g.group}`}>
        <td className="px-3 py-1"></td>
        <td className="px-3 py-1 text-center font-bold">{g.group}</td>
        <td className="px-3 py-1 text-center font-bold font-mono text-emerald-800">{g.dep_qty}</td>
        <td className="px-3 py-1 text-center font-bold font-mono text-sky-800">{g.nondep_qty}</td>
        <td className="px-3 py-1 text-center font-bold font-mono">{g.group_qty}</td>
        <td></td>
      </tr>
      {g.rows.map((r) => (
        <StockRow key={r.product_id} r={r} canWrite={canWrite} onChanged={onChanged} showBrand={showBrand} />))}
    </>
  );
}

/** GOOD MORNING — STOCK BRIEF. Rendered on the internal home; shows once per
 *  user per day, dismissal stored server-side. */
export function MorningStockBrief() {
  const [d, setD] = useState(null);
  const [hidden, setHidden] = useState(false);
  useEffect(() => {
    api.get("/inventory/morning-brief").then(({ data }) => setD(data)).catch(() => setHidden(true));
  }, []);
  if (hidden || !d || d.dismissed) return null;
  const p = d.position || {};
  const y = d.yesterday || {};
  const dismiss = async () => {
    setHidden(true);
    try { await api.post("/inventory/morning-brief/dismiss"); } catch { /* ignore */ }
  };
  return (
    <div className="border border-emerald-200 bg-gradient-to-br from-emerald-50 to-white rounded-xl p-4 mb-4"
        data-testid="morning-stock-brief">
      <div className="flex items-center gap-2 mb-3">
        <Sunrise className="h-4 w-4 text-amber-500" />
        <div className="text-[13px] font-heading font-semibold">Good morning — Stock Brief</div>
        <span className="text-[10px] text-slate-500">{d.date}</span>
        <div className="flex-1" />
        <a href="/operations/stock-inventory" className="text-[10.5px] px-2 py-1 rounded border border-emerald-600 text-emerald-800"
            data-testid="brief-view-full">View full stock</a>
        <button onClick={dismiss} data-testid="brief-dismiss"
            className="text-[10.5px] px-2 py-1 rounded border border-slate-300 text-slate-600">Dismiss for today</button>
      </div>
      <div className="grid grid-cols-3 md:grid-cols-7 gap-2 text-center">
        {[["SKUs", p.total_skus], ["Units", p.on_hand], ["Available", p.available],
          ["Reserved", p.reserved], ["In transit", p.in_transit],
          ["Customer-owned", p.customer_owned], ["Repair/Quar.", p.repair_quarantine]]
          .map(([l, v]) => (
            <div key={l} className="bg-white border border-slate-200 rounded-lg p-2">
              <div className="text-[9px] uppercase text-slate-500">{l}</div>
              <div className="text-[15px] font-heading font-semibold">{v ?? 0}</div>
            </div>))}
      </div>
      <div className="grid md:grid-cols-2 gap-3 mt-3">
        <div className="text-[11px]">
          <div className="font-semibold text-slate-700 mb-1">Yesterday</div>
          <div className="text-slate-600">
            In {y.stock_in ?? 0} · Out {y.stock_out ?? 0} · Dispatched {y.dispatched ?? 0} ·
            Returns {y.returns ?? 0} · Transfers {y.transfers ?? 0} · Adjustments {y.adjustments ?? 0}
          </div>
        </div>
        <div className="text-[11px]" data-testid="brief-actions">
          <div className="font-semibold text-slate-700 mb-1">Today — action required</div>
          {(d.actions || []).length === 0
            ? <div className="text-slate-500">No inventory exceptions — all clear.</div>
            : (d.actions || []).map((a) => (
                <div key={a.kind} className="text-amber-800">• {a.label}</div>))}
        </div>
      </div>
    </div>
  );
}
